/** Full CCS Spotlight article renderer — used for the featured story and /spotlight/:id. */
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Linkedin, Facebook, Mail, Clock, ArrowRight } from "lucide-react";
import { categoryBadge, formatSpotlightDate, shareLinks } from "./spotlightUtils";
import SignatureBlock from "@/components/brand/SignatureBlock";
import RichText from "@/components/RichText";

const Section = ({ overline, children, testid }) => (
  <motion.div
    initial={{ opacity: 0, y: 18 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-60px" }}
    transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
    className="mt-12"
    data-testid={testid}
  >
    <div className="text-xs uppercase tracking-[0.25em] font-semibold text-[#C28A2E] mb-4">{overline}</div>
    <RichText content={children} className="text-base sm:text-lg leading-[1.85] text-[#2A1810]/85" />
  </motion.div>
);

export const ShareRow = ({ spotlight }) => {
  const links = shareLinks(spotlight);
  const cls =
    "w-10 h-10 rounded-full border border-[#2A1810]/15 flex items-center justify-center text-[#5D3A1F] hover:border-[#C28A2E] hover:text-[#C28A2E] transition-colors duration-300";
  return (
    <div className="flex items-center gap-3" data-testid="spotlight-share-row">
      <span className="text-xs uppercase tracking-[0.2em] font-semibold text-[#6B5944] mr-1">Share</span>
      <a href={links.linkedin} target="_blank" rel="noopener noreferrer" aria-label="Share on LinkedIn" className={cls} data-testid="share-linkedin">
        <Linkedin size={16} strokeWidth={1.5} />
      </a>
      <a href={links.facebook} target="_blank" rel="noopener noreferrer" aria-label="Share on Facebook" className={cls} data-testid="share-facebook">
        <Facebook size={16} strokeWidth={1.5} />
      </a>
      <a href={links.email} aria-label="Share by email" className={cls} data-testid="share-email">
        <Mail size={16} strokeWidth={1.5} />
      </a>
    </div>
  );
};

export const SpotlightArticle = ({ spotlight, onNominate }) => (
  <article data-testid="spotlight-article">
    {/* Meta header */}
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-end">
      <div className="lg:col-span-7">
        <img
          src={spotlight.image_url}
          alt={spotlight.honoree_name}
          className="block w-auto max-w-full h-auto max-h-[80vh] mx-auto rounded-sm border border-[#2A1810]/5"
          data-testid="spotlight-feature-image"
        />
      </div>
      <div className="lg:col-span-5">
        <span className={`inline-block text-[11px] uppercase tracking-[0.18em] font-bold px-3 py-1.5 rounded-full ${categoryBadge(spotlight.category)}`} data-testid="spotlight-category-badge">
          {spotlight.category}
        </span>
        <h2 className="mt-5 font-display text-3xl sm:text-4xl font-medium tracking-tight leading-tight text-[#2A1810]" data-testid="spotlight-title">
          {spotlight.title || spotlight.honoree_name}
        </h2>
        <p className="mt-4 text-lg text-[#5D3A1F] font-medium" data-testid="spotlight-honoree">{spotlight.honoree_name}</p>
        {spotlight.organization && (
          <p className="text-sm text-[#6B5944]" data-testid="spotlight-organization">{spotlight.organization}</p>
        )}
        <div className="mt-5 flex items-center gap-4 text-xs text-[#6B5944]">
          <span data-testid="spotlight-date">{formatSpotlightDate(spotlight.published_at)}</span>
          <span className="w-1 h-1 rounded-full bg-[#C28A2E]" />
          <span className="inline-flex items-center gap-1.5" data-testid="spotlight-reading-time">
            <Clock size={13} strokeWidth={1.5} /> {spotlight.reading_time} min read
          </span>
        </div>
        <div className="mt-7">
          <ShareRow spotlight={spotlight} />
        </div>
      </div>
    </div>

    {/* Story body — narrow reading column */}
    <div className="max-w-3xl mt-4">
      <Section overline="What Happened" testid="section-what-happened">{spotlight.what_happened}</Section>
      <Section overline="Why This Matters" testid="section-why-this-matters">{spotlight.why_this_matters}</Section>

      {/* Signature CCS Lesson block */}
      <motion.div
        initial={{ opacity: 0, y: 18 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-60px" }}
        transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
        className="mt-12 bg-[#F6F3EB] border-l-2 border-[#C28A2E] rounded-r-sm px-8 py-8"
        data-testid="section-ccs-lesson"
      >
        <div className="text-xs uppercase tracking-[0.25em] font-semibold text-[#C28A2E] mb-4">The CCS Lesson</div>
        <RichText content={spotlight.ccs_lesson} className="text-base sm:text-lg leading-[1.85] text-[#5D3A1F]" />
      </motion.div>

    {/* CCS Reflection */}
    <motion.blockquote
      initial={{ opacity: 0 }}
      whileInView={{ opacity: 1 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.9 }}
      className="mt-16 text-center"
      data-testid="section-ccs-reflection"
    >
      <div className="text-xs uppercase tracking-[0.25em] font-semibold text-[#C28A2E] mb-6">CCS Reflection</div>
      <p className="font-display text-xl sm:text-2xl italic font-light leading-relaxed text-[#5D3A1F] max-w-2xl mx-auto">
        “{spotlight.ccs_reflection}”
      </p>
    </motion.blockquote>

    {/* Calls to action — below story content, above the signature */}
    <div className="mt-14 pt-10 border-t border-[#EBE3D2] text-center" data-testid="spotlight-cta-block">
      <p className="text-sm sm:text-base text-[#5D3A1F]">Know someone who deserves to be recognized?</p>
      <button
        type="button"
        onClick={onNominate}
        className="mt-5 inline-flex items-center gap-2 bg-[#C28A2E] text-[#2A1810] px-8 py-3.5 rounded-full text-sm font-bold tracking-wide hover:bg-[#E7B54D] transition-colors duration-300"
        data-testid="spotlight-nominate-cta"
      >
        Submit a Nomination <ArrowRight className="w-4 h-4" />
      </button>
      <p className="mt-8 text-sm text-[#6B5944]">Want your team to serve like this?</p>
      <Link
        to="/book#booking"
        className="mt-3 inline-flex items-center gap-2 border border-[#5D3A1F]/35 text-[#5D3A1F] px-6 py-2.5 rounded-full text-xs font-bold tracking-wide hover:border-[#C28A2E] hover:text-[#C28A2E] transition-colors duration-300"
        data-testid="spotlight-book-cta"
      >
        Book a free discovery call
      </Link>
    </div>

    <div className="text-center">
      <SignatureBlock className="mt-12" />
    </div>
  </div>
  </article>
);

export default SpotlightArticle;
